import { useState, useRef, useCallback } from "react";
import { Drawer } from "./Drawer";
import { Button } from "../atoms/Button";
import { Select } from "../atoms/Select";
import { useContractCategories } from "../../hooks/useContractCategories";
import { FileText, Upload } from "lucide-react";

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

type UploadContractDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onUpload: (file: File, contractCategory: string) => Promise<void>;
};

const MAX_SIZE_MB = 10;
const ACCEPTED_TYPES = new Set([
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

export const UploadContractDrawer = ({ isOpen, onClose, onUpload }: UploadContractDrawerProps) => {
  const { categories: contractCategories, loading: categoriesLoading } = useContractCategories();
  const [file, setFile] = useState<File | null>(null);
  const [contractCategory, setContractCategory] = useState("");
  const [contractCategoryError, setContractCategoryError] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setFile(null);
    setContractCategory("");
    setContractCategoryError(undefined);
    setError(null);
    setIsUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const validateFile = useCallback((f: File): string | null => {
    if (!ACCEPTED_TYPES.has(f.type)) {
      return "Unsupported format. Please upload DOC, or DOCX file.";
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      return `File exceeds ${MAX_SIZE_MB}MB limit.`;
    }
    return null;
  }, []);

  const handleFile = useCallback(
    (f: File) => {
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        setFile(null);
        return;
      }
      setError(null);
      setFile(f);
    },
    [validateFile],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0] ?? null;
      if (selected) handleFile(selected);
    },
    [handleFile],
  );

  const validateForm = useCallback((): boolean => {
    if (contractCategory.trim()) {
      setContractCategoryError(undefined);
      return true;
    }
    setContractCategoryError("Contract category is required");
    return false;
  }, [contractCategory]);

  const handleSubmit = useCallback(async () => {
    if (!file) return;
    if (!validateForm()) return;
    setIsUploading(true);
    setError(null);
    try {
      await onUpload(file, contractCategory.trim());
      resetState();
    } catch {
      setError("Upload failed. Please try again.");
      setIsUploading(false);
    }
  }, [file, contractCategory, validateForm, onUpload, resetState]);

  const isValid = file !== null && contractCategory.trim() !== "";

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title="Upload Contract">
      <div className="flex h-full flex-col">
        <p className="-mt-2 mb-6 text-sm text-gray-500">
          Upload a contract document for template extraction
        </p>

        <div className="flex flex-1 flex-col gap-6">
          <Select
            label="Contract Category"
            options={[...contractCategories]}
            value={contractCategory}
            onChange={(e) => {
              setContractCategory(e.target.value);
              setContractCategoryError(undefined);
            }}
            error={contractCategoryError}
            required
            disabled={categoriesLoading}
            placeholder={categoriesLoading ? "Loading categories…" : "Select contract category"}
          />

          <div className="space-y-1.5">
            <label htmlFor="contract-upload-input" className="form-label">
              Upload File<span className="form-label-asterisk">*</span>
            </label>

            {file ? (
              <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-4 transition-colors">
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100">
                    <FileText className="h-5 w-5 text-primary-600" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">{file.name}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setError(null);
                      if (inputRef.current) inputRef.current.value = "";
                    }}
                    className="shrink-0 text-sm font-medium text-primary-500 transition-colors duration-200 hover:text-primary-600"
                  >
                    Remove file
                  </button>
                </div>
              </div>
            ) : (
              <label
                htmlFor="contract-upload-input"
                className="flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-gray-300 bg-white px-6 py-10 text-center transition-all duration-200"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
                  <Upload className="h-7 w-7 text-primary-500" strokeWidth={1.75} />
                </div>

                <p className="mb-1 text-base font-semibold text-gray-900">
                  Upload Contract Document
                </p>
                <p className="mb-4 text-sm text-gray-500">Click to upload your document.</p>

                <span className="mb-4 inline-flex items-center justify-center rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-600">
                  Browse Files
                </span>

                <p className="text-xs text-gray-400">
                  Supported formats: DOCX (Max {MAX_SIZE_MB}MB)
                </p>
              </label>
            )}
          </div>

          {error && <p className="text-sm text-status-error">{error}</p>}

          <div className="rounded-xl border border-gray-200 bg-primary-50 p-5 transition-colors">
            <p className="mb-3 text-sm font-semibold text-gray-900">What happens next?</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
                  aria-hidden="true"
                />
                <span>AI will extract and categorize clauses from your contract</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
                  aria-hidden="true"
                />
                <span>Review and edit extracted clauses as needed</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
                  aria-hidden="true"
                />
                <span>Save the contract template for future use</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
          <Button
            variant="ghost"
            onClick={handleClose}
            className="border border-gray-300 bg-white text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isUploading}>
            {isUploading ? "Uploading…" : "Upload & Analyze"}
          </Button>
        </div>

        <input
          ref={inputRef}
          id="contract-upload-input"
          type="file"
          accept=".doc,.docx"
          onChange={handleChange}
          className="hidden"
          tabIndex={-1}
        />
      </div>
    </Drawer>
  );
};
